import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { AurumOrb } from "@/components/brand/AurumOrb";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";

const CHOICE = (opts) => opts;

const STEPS = [
  { key: "experience", title: "Your trading experience", type: "choice", options: CHOICE(["Beginner", "Intermediate", "Advanced"]) },
  { key: "goal", title: "What's your primary goal?", type: "choice", options: CHOICE(["Learn", "Discipline", "Monitor Setups", "Risk Management", "Paper Trading"]) },
  { key: "style", title: "Your trading style", type: "choice", options: CHOICE(["Scalp", "Intraday", "Swing"]) },
  { key: "account", title: "Account simulation settings", type: "account" },
  { key: "risk", title: "Your risk plan", type: "risk" },
  { key: "schedule", title: "Trading schedule", type: "schedule" },
  { key: "strategy", title: "Preferred strategy", type: "choice", options: CHOICE(["Trend Pullback", "Trend Following", "Breakout + Retest", "Range Trading", "Mean Reversion"]), field: "preferred_strategy" },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState({
    experience: "Beginner", goal: "Discipline", style: "Intraday",
    account_size: 10000, account_currency: "USD",
    max_risk_per_trade: 0.5, max_daily_loss: 300, max_weekly_loss: 800,
    max_trades_per_day: 3, max_consecutive_losses: 3,
    preferred_session: "London", timezone: "UTC", preferred_strategy: "Trend Pullback",
  });

  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  const setField = (k, v) => setPlan((p) => ({ ...p, [k]: v }));

  const next = async () => {
    if (!last) { setStep(step + 1); return; }
    setGenerating(true);
    try {
      await api.post("/plan", plan);
      await refreshUser();
      toast.success("Your trading plan is ready.");
      setTimeout(() => nav("/app"), 900);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] text-[var(--text)] gap-6">
        <AurumOrb status="ANALYZING" size={120} />
        <div className="text-center">
          <div className="text-lg font-semibold">Generating your trading plan…</div>
          <div className="text-sm text-[var(--text-muted)] mt-1">Configuring risk limits and monitoring rules.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <div className="h-16 flex items-center px-6 border-b border-[var(--border2)]"><Logo /></div>
      <div className="mx-auto max-w-2xl px-6 py-10">
        <div className="flex items-center gap-1.5 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full transition-colors"
              style={{ background: i <= step ? "#D6AE57" : "var(--border2)" }} />
          ))}
        </div>
        <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-2">
          Step {step + 1} of {STEPS.length}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={s.key} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <h1 className="text-2xl font-bold tracking-tight">{s.title}</h1>

            {s.type === "choice" && (
              <div className="mt-6 grid gap-3">
                {s.options.map((opt) => {
                  const field = s.field || s.key;
                  const active = plan[field] === opt;
                  return (
                    <button key={opt} data-testid={`onboard-${field}-${opt.replace(/\s/g, "-")}`}
                      onClick={() => setField(field, opt)}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3.5 text-left transition-all ${
                        active ? "border-aurum bg-[rgba(214,174,87,0.08)]" : "border-[var(--border2)] hover:border-[var(--text-muted)]"
                      }`}>
                      <span className="text-sm font-medium">{opt}</span>
                      {active && <Check size={18} className="text-aurum" />}
                    </button>
                  );
                })}
              </div>
            )}

            {s.type === "account" && (
              <div className="mt-6 space-y-4">
                <Field label="Simulated account size (USD)" testId="onboard-account-size" type="number"
                  value={plan.account_size} onChange={(v) => setField("account_size", Number(v))} />
                <Field label="Account currency" value={plan.account_currency}
                  onChange={(v) => setField("account_currency", v)} />
              </div>
            )}

            {s.type === "risk" && (
              <div className="mt-6 space-y-4">
                <Field label="Max risk per trade (%)" testId="onboard-risk-pct" type="number" step="0.1"
                  value={plan.max_risk_per_trade} onChange={(v) => setField("max_risk_per_trade", Number(v))} />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Max daily loss ($)" type="number" value={plan.max_daily_loss} onChange={(v) => setField("max_daily_loss", Number(v))} />
                  <Field label="Max weekly loss ($)" type="number" value={plan.max_weekly_loss} onChange={(v) => setField("max_weekly_loss", Number(v))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Max trades / day" type="number" step="1" value={plan.max_trades_per_day} onChange={(v) => setField("max_trades_per_day", Math.max(1, Math.round(Number(v))))} />
                  <Field label="Max consecutive losses" type="number" step="1" value={plan.max_consecutive_losses} onChange={(v) => setField("max_consecutive_losses", Math.max(1, Math.round(Number(v))))} />
                </div>
                <p className="text-xs text-[var(--text-muted)]">Aurum defaults beginners to 0.5% per trade. These limits act as a circuit breaker.</p>
              </div>
            )}

            {s.type === "schedule" && (
              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-sm text-[var(--text-sec)]">Preferred session</label>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {["Asian", "London", "New York"].map((sess) => (
                      <button key={sess} onClick={() => setField("preferred_session", sess)}
                        className={`rounded-lg border px-3 py-2 text-sm transition-all ${plan.preferred_session === sess ? "border-aurum bg-[rgba(214,174,87,0.08)]" : "border-[var(--border2)]"}`}>
                        {sess}
                      </button>
                    ))}
                  </div>
                </div>
                <Field label="Timezone" value={plan.timezone} onChange={(v) => setField("timezone", v)} />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text)] disabled:opacity-40">
            <ArrowLeft size={16} /> Back
          </button>
          <button onClick={next} data-testid="onboard-next"
            className="btn-gold flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all hover:brightness-110">
            {last ? "Generate my plan" : "Continue"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, testId }) {
  return (
    <div>
      <label className="text-sm text-[var(--text-sec)]">{label}</label>
      <input data-testid={testId} type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2.5 text-sm outline-none focus:border-aurum transition-colors tnum" />
    </div>
  );
}
